import {
  AlertTriangle,
  CalendarDays,
  FileText,
  LoaderCircle,
  Mic,
  NotebookPen,
  Pencil,
  Search,
  Send,
  ShieldCheck,
  Square,
  Trash2,
  X
} from "lucide-react";
import { useState } from "react";
import { useVoiceCapture } from "../lib/useVoiceCapture";
import type { Destination, Note, ProcessedCapture } from "../types";
import { TurnstileWidget } from "./TurnstileWidget";

const destinations: Array<{
  id: Exclude<Destination, "unknown">;
  label: string;
  hint: string;
  icon: typeof NotebookPen;
}> = [
  { id: "note", label: "Note ParleNote", hint: "Dans Mes notes", icon: NotebookPen },
  { id: "google_docs", label: "Document Google", hint: "Nouveau document", icon: FileText },
  { id: "calendar", label: "Google Agenda", hint: "Événement ou rappel", icon: CalendarDays }
];

function formatSlot(start: string, end: string): string {
  const from = new Date(start);
  const to = new Date(end);
  if (Number.isNaN(from.getTime())) return "";
  const day = new Intl.DateTimeFormat("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short"
  }).format(from);
  const clock = new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return Number.isNaN(to.getTime())
    ? `${day} ${clock.format(from)}`
    : `${day} ${clock.format(from)}–${clock.format(to)}`;
}

function FollowUp({
  question,
  onAnswer
}: {
  question: ProcessedCapture["questions"][number];
  onAnswer: (id: string, answer: string) => void;
}) {
  const [custom, setCustom] = useState("");
  const inputType = question.id === "date" ? "date" : question.id === "time" ? "time" : "text";
  const options = question.options || [];
  return (
    <fieldset className="follow-up">
      <legend>{question.prompt}</legend>
      {/* An empty grid used to render as a blank gap above the input. */}
      {options.length > 0 && (
        <div className="answer-grid">
          {options.map((option) => (
            <button
              className="answer-option"
              type="button"
              key={option}
              onClick={() => onAnswer(question.id, option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
      <div className="custom-answer">
        <label htmlFor={`custom-${question.id}`}>
          {options.length > 0 ? "Autre réponse" : "Votre réponse"}
        </label>
        <input
          id={`custom-${question.id}`}
          type={inputType}
          value={custom}
          onChange={(event) => setCustom(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && custom) {
              event.preventDefault();
              onAnswer(question.id, custom);
            }
          }}
        />
        <button
          className="button compact ghost"
          type="button"
          disabled={!custom}
          onClick={() => onAnswer(question.id, custom)}
        >
          Valider
        </button>
      </div>
    </fieldset>
  );
}

export function ReviewView({
  capture,
  busy,
  autoCountdown,
  onChange,
  onDestination,
  onAnswer,
  onEventChange,
  onCancelAuto,
  onCancel,
  onConfirm,
  onFollowUp,
  turnstileSiteKey,
  targetNote
}: {
  capture: ProcessedCapture;
  busy: boolean;
  autoCountdown?: number;
  onChange: (field: "title" | "body", value: string) => void;
  onDestination: (destination: Exclude<Destination, "unknown">) => void;
  onAnswer: (id: string, answer: string) => void;
  onEventChange: (field: "date" | "time" | "durationMinutes", value: string) => void;
  onCancelAuto: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  onFollowUp: (text: string, audio?: Blob, turnstileToken?: string) => Promise<boolean>;
  turnstileSiteKey?: string;
  /** The note this draft modifies, so its current text stays visible. */
  targetNote?: Note;
}) {
  const deleting = capture.operation === "delete" && capture.destination === "note";
  const editing = capture.operation === "append" || capture.operation === "update";
  const ready = deleting
    ? // A deletion needs the note itself, not a body to write.
      Boolean(targetNote)
    : capture.questions.length === 0 &&
      capture.destination !== "unknown" &&
      capture.title.trim().length > 0 &&
      (capture.destination === "calendar"
        ? Boolean(capture.event?.date && capture.event?.time)
        : capture.body.trim().length > 0);
  // The title is answered by the field above, not by a question below it.
  const titleQuestion = capture.questions.find((question) => question.id === "title");
  const openQuestions = capture.questions.filter((question) => question.id !== "title");
  const reply = useVoiceCapture(120);
  const [replyText, setReplyText] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string>();
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);

  const sendFollowUp = async () => {
    const processed = await onFollowUp(replyText || reply.transcript, reply.audio, turnstileToken);
    // The token is consumed by the attempt itself, successful or not.
    if (turnstileSiteKey) {
      setTurnstileToken(undefined);
      setTurnstileAttempt((value) => value + 1);
    }
    if (!processed) return;
    setReplyText("");
    reply.reset();
  };

  // A deletion has nothing to edit and everything to check, so the screen shows
  // the note as it stands and asks one unambiguous question.
  if (deleting) {
    return (
      <section className="review-view" aria-labelledby="review-title">
        <button className="close-button" type="button" onClick={onCancel} aria-label="Annuler">
          <X aria-hidden="true" />
        </button>
        <h1 id="review-title">Supprimer cette note&nbsp;?</h1>
        <p className="review-intro">
          {targetNote
            ? "Relisez-la : la suppression est définitive."
            : `Je ne retrouve pas la note « ${capture.targetTitle || capture.title} ». Rien ne sera supprimé.`}
        </p>

        {targetNote && (
          <div className="existing-note is-doomed">
            <div className="existing-note-heading">
              <Trash2 size={16} aria-hidden="true" />
              <strong>{targetNote.title}</strong>
              <span>sera supprimée</span>
            </div>
            <p>{targetNote.body}</p>
          </div>
        )}

        <div className="safety-summary">
          <ShieldCheck aria-hidden="true" />
          <span>Rien n’a encore été supprimé.</span>
        </div>

        <div className="sticky-actions">
          <button className="button ghost" type="button" onClick={onCancel}>
            Annuler
          </button>
          <button
            className="button danger"
            type="button"
            disabled={!ready || busy}
            onClick={onConfirm}
          >
            <Trash2 size={17} aria-hidden="true" />
            {busy ? "Suppression…" : "Supprimer définitivement"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="review-view" aria-labelledby="review-title">
      <button className="close-button" type="button" onClick={onCancel} aria-label="Annuler">
        <X aria-hidden="true" />
      </button>
      <h1 id="review-title">Voici ce que j’ai compris</h1>
      <p className="review-intro">
        {capture.operation === "append"
          ? `Ajout à la note « ${capture.targetTitle || capture.title} » — confirmez avant modification.`
          : capture.operation === "update"
            ? `Modification de la note « ${capture.targetTitle || capture.title} » — confirmez avant remplacement.`
            : "Vous pouvez tout corriger avant la suite."}
      </p>

      {/* A "quel titre ?" question below an empty Titre field asked the same
          thing twice, in two places, with two inputs. The field itself carries
          the request now. */}
      <div className={`editable-field ${titleQuestion ? "needs-attention" : ""}`}>
        <label htmlFor="note-title">Titre</label>
        <input
          id="note-title"
          value={capture.title}
          autoFocus={Boolean(titleQuestion)}
          aria-describedby={titleQuestion ? "note-title-hint" : undefined}
          placeholder={titleQuestion ? "Donnez un titre à cette note" : undefined}
          onChange={(event) => onChange("title", event.target.value)}
        />
        <Pencil aria-hidden="true" size={17} />
      </div>
      {titleQuestion && (
        <p className="field-request" id="note-title-hint">
          {titleQuestion.prompt}
        </p>
      )}
      {/* An append showed only the new lines, so a note full of existing text
          looked empty and indistinguishable from a brand new one. */}
      {editing && targetNote && (
        <div className="existing-note">
          <div className="existing-note-heading">
            <NotebookPen size={16} aria-hidden="true" />
            <strong>Contenu actuel de « {targetNote.title} »</strong>
            <span>
              {capture.operation === "append" ? "sera conservé" : "sera remplacé"}
            </span>
          </div>
          <p>{targetNote.body}</p>
        </div>
      )}

      <div className="editable-field body">
        <label htmlFor="note-body">
          {editing
            ? capture.operation === "append"
              ? "Ce qui sera ajouté à la suite"
              : "Le nouveau contenu, à la place de l’actuel"
            : "Contenu"}
        </label>
        <textarea
          id="note-body"
          rows={5}
          value={capture.body}
          onChange={(event) => onChange("body", event.target.value)}
        />
        <Pencil aria-hidden="true" size={17} />
      </div>

      {editing && targetNote && capture.operation === "append" && capture.body.trim() && (
        <details className="merge-preview">
          <summary>Aperçu de la note après l’ajout</summary>
          <p>{[targetNote.body.trim(), capture.body.trim()].filter(Boolean).join("\n\n")}</p>
        </details>
      )}

      <fieldset className="destination-fieldset">
        <legend>Où l’envoyer&nbsp;?</legend>
        <div className="destination-list">
          {destinations.map(({ id, label, hint, icon: Icon }) => (
            <label className="destination-option" key={id}>
              <input
                type="radio"
                name="destination"
                value={id}
                checked={capture.destination === id}
                onChange={() => onDestination(id)}
              />
              <Icon size={22} aria-hidden="true" />
              <span>
                <strong>{label}</strong>
                <small>{hint}</small>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {capture.destination === "calendar" && (
        <fieldset className="event-details">
          <legend>Date et heure</legend>
          <label>
            Date
            <input
              type="date"
              value={capture.event?.date || ""}
              onChange={(event) => onEventChange("date", event.target.value)}
            />
          </label>
          <label>
            Heure
            <input
              type="time"
              value={capture.event?.time || ""}
              onChange={(event) => onEventChange("time", event.target.value)}
            />
          </label>
          <label>
            Durée
            <select
              value={String(capture.event?.durationMinutes || 30)}
              onChange={(event) => onEventChange("durationMinutes", event.target.value)}
            >
              <option value="15">15 min</option>
              <option value="30">30 min</option>
              <option value="45">45 min</option>
              <option value="60">1 h</option>
              <option value="90">1 h 30</option>
            </select>
          </label>
        </fieldset>
      )}

      {capture.conflicts && capture.conflicts.length > 0 && (
        <div className="agent-conflict" role="alert">
          <AlertTriangle aria-hidden="true" />
          <div>
            <strong>Ce créneau est déjà pris</strong>
            <ul>
              {capture.conflicts.map((conflict) => (
                <li key={`${conflict.start}-${conflict.title}`}>
                  {conflict.title} — {formatSlot(conflict.start, conflict.end)}
                </li>
              ))}
            </ul>
            <small>Vous pouvez confirmer quand même ou changer l’heure ci-dessus.</small>
          </div>
        </div>
      )}

      {capture.references && capture.references.length > 0 && (
        <div className="agent-evidence">
          <Search aria-hidden="true" />
          <span>
            J’ai consulté&nbsp;:{" "}
            {capture.references.map((note) => note.title || "note sans titre").join(", ")}
          </span>
        </div>
      )}

      {openQuestions.map((question) => (
        <FollowUp question={question} onAnswer={onAnswer} key={question.id} />
      ))}

      {openQuestions.length > 0 && (
        <section className="assistant-reply" aria-labelledby="assistant-reply-title">
          <div>
            <strong id="assistant-reply-title">Répondre naturellement</strong>
            <span>Vous pouvez parler ou écrire, je garderai le contexte.</span>
          </div>
          <div className="assistant-composer">
            <button
              className={`mini-mic ${reply.status === "recording" ? "active" : ""}`}
              type="button"
              aria-label={
                reply.status === "recording" ? "Arrêter la réponse vocale" : "Répondre à voix haute"
              }
              onClick={async () => {
                if (reply.status === "recording") {
                  const result = await reply.stop();
                  if (result.transcript.trim()) setReplyText(result.transcript);
                } else {
                  setReplyText("");
                  await reply.start();
                }
              }}
            >
              {reply.status === "recording" ? (
                <Square fill="currentColor" aria-hidden="true" />
              ) : (
                <Mic aria-hidden="true" />
              )}
            </button>
            <textarea
              rows={2}
              value={replyText || reply.transcript}
              onChange={(event) => setReplyText(event.target.value)}
              placeholder="Ex. Demain à 14 h, dans Google Agenda"
            />
            <button
              className="icon-button send-reply"
              type="button"
              aria-label="Envoyer la réponse"
              disabled={
                busy ||
                (!replyText.trim() && !reply.transcript.trim() && !reply.audio) ||
                (Boolean(turnstileSiteKey) && !turnstileToken)
              }
              onClick={() => void sendFollowUp()}
            >
              {busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
            </button>
          </div>
          {reply.error && <p className="inline-error">{reply.error}</p>}
          {turnstileSiteKey && (
            <TurnstileWidget
              key={turnstileAttempt}
              siteKey={turnstileSiteKey}
              onToken={setTurnstileToken}
            />
          )}
        </section>
      )}


      {autoCountdown !== undefined ? (
        <div className="auto-banner" role="status">
          <div>
            <strong>Création automatique dans {autoCountdown} s</strong>
            <span>Ce mode a été activé dans vos permissions.</span>
          </div>
          <button type="button" onClick={onCancelAuto}>
            Annuler
          </button>
        </div>
      ) : (
        <div className="safety-summary">
          <ShieldCheck aria-hidden="true" />
          <span>Aucune action n’a encore été effectuée.</span>
        </div>
      )}

      <div className="sticky-actions">
        <button className="button ghost" type="button" onClick={onCancel}>
          Annuler
        </button>
        <button
          className="button primary"
          type="button"
          disabled={!ready || busy}
          onClick={onConfirm}
        >
          {busy ? "Création…" : "Confirmer"}
        </button>
      </div>
    </section>
  );
}
